import {
  type ActionEventArgs,
  type EventClickArgs,
  type EventRenderedArgs,
  type PopupOpenEventArgs,
  type ScheduleComponent,
} from '@syncfusion/ej2-react-schedule'
import dayjs from 'dayjs'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'
import type { AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import type { StatusColorName } from 'features/SettingsOld/state/api/types'
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react'
import { type AppointmentColors, applyAppointmentColors } from './applyAppointmentColors'
import CalendarEvent from './CalendarEvent'
import {
  applyScheduleColumnWidths,
  getAppointmentColor,
  getAppointmentColorInputFromEvent,
  getMaxConcurrentByColumnKey,
  stretchRightmostAppointmentsToColumnEdge,
} from './helpers'
import { safeRefreshScheduleEvents } from './safeRefreshScheduleEvents'

type ScheduleEventData = IPatientSchedule & {
  status?: { code?: number }
  category?: { code?: number }
}

type UseScheduleViewParams = {
  data: IPatientSchedule[]
  schedules: IPatientSchedule[]
  scheduleObj: RefObject<ScheduleComponent | null>
  interval: number
  dayInterval: { min: string; max: string }
  day?: string
  timeZone?: string
  appointmentColor: AppointmentColorType
  appointmentCategories: AppointmentCategories[]
  patientStatuses: StatusColorName[]
  hasEhrBooking: boolean
  showConsultationStaffInitials: boolean
  isWeekView?: boolean
  columnKeys?: string[]
  setEditedPatient: (patient: IPatientSchedule | null) => void
  handleScheduleChange: (data: IPatientSchedule) => Promise<void>
}

const findEventById = (
  events: IPatientSchedule[],
  id: number | string | undefined,
): IPatientSchedule | undefined => {
  if (id == null) return undefined
  return events.find((event) => event.id === id || Number(event.id) === Number(id))
}

/**
 * Shared Syncfusion scheduler wiring used by both the day and week calendars:
 * appointment colouring, the event template, and the common event handlers/config.
 * View-specific concerns (resources, render-cell rules, selection) stay in each view.
 */
export const useScheduleView = ({
  data,
  schedules,
  scheduleObj,
  interval,
  dayInterval,
  day,
  timeZone,
  appointmentColor,
  appointmentCategories,
  patientStatuses,
  hasEhrBooking,
  showConsultationStaffInitials,
  isWeekView = false,
  columnKeys = [],
  setEditedPatient,
  handleScheduleChange,
}: UseScheduleViewParams) => {
  const dataRef = useRef(data)
  const schedulesRef = useRef(schedules)
  const columnKeysRef = useRef(columnKeys)

  useEffect(() => {
    dataRef.current = data
  }, [data])

  useEffect(() => {
    schedulesRef.current = schedules
  }, [schedules])

  useEffect(() => {
    columnKeysRef.current = columnKeys
  }, [columnKeys])

  const applyDynamicColumnWidths = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    let keys = columnKeysRef.current
    if (isWeekView && keys.length === 0) {
      keys = schedule
        .getCurrentViewDates()
        .map((date) =>
          timeZone
            ? dayjs(date).tz(timeZone).format('YYYY-MM-DD')
            : dayjs(date).format('YYYY-MM-DD'),
        )
    }
    if (keys.length > 0) {
      applyScheduleColumnWidths({
        scheduleElement: schedule.element,
        columnKeys: keys,
        maxConcurrentByColumn: getMaxConcurrentByColumnKey(dataRef.current),
      })
    }

    stretchRightmostAppointmentsToColumnEdge(schedule.element)
  }, [scheduleObj, isWeekView, timeZone])

  const getEventColors = useCallback(
    (eventData: IPatientSchedule): AppointmentColors => {
      const live =
        findEventById(dataRef.current, eventData.id) ??
        findEventById(schedulesRef.current, eventData.id) ??
        eventData

      const { statusCode, categoryCode } = getAppointmentColorInputFromEvent(
        live,
        schedulesRef.current,
      )
      return getAppointmentColor({
        appointmentColor,
        categoryStatuses: appointmentCategories,
        statusColorName: patientStatuses,
        statusCode,
        categoryCode,
      })
    },
    [appointmentColor, appointmentCategories, patientStatuses],
  )

  const getEventColorsRef = useRef(getEventColors)
  getEventColorsRef.current = getEventColors

  const reapplyAppointmentColors = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    for (const event of dataRef.current) {
      const colors = getEventColorsRef.current(event)
      schedule.element
        .querySelectorAll<HTMLElement>(
          `[data-id="Appointment_${event.id}"]:not(.e-schedule-event-clone)`,
        )
        .forEach((element) => applyAppointmentColors(element, colors))
    }
  }, [scheduleObj])

  const eventRendered = useCallback((props: EventRenderedArgs) => {
    applyAppointmentColors(props.element, getEventColorsRef.current(props.data as IPatientSchedule))
  }, [])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      safeRefreshScheduleEvents(scheduleObj.current)
      reapplyAppointmentColors()
      applyDynamicColumnWidths()
    })
    return () => cancelAnimationFrame(frameId)
  }, [data, scheduleObj, reapplyAppointmentColors, applyDynamicColumnWidths])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      applyDynamicColumnWidths()
    })
    return () => cancelAnimationFrame(frameId)
  }, [columnKeys, applyDynamicColumnWidths])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 576px)')
    const handleBreakpointChange = () => applyDynamicColumnWidths()
    mediaQuery.addEventListener('change', handleBreakpointChange)
    return () => mediaQuery.removeEventListener('change', handleBreakpointChange)
  }, [applyDynamicColumnWidths])

  const eventTemplate = useCallback(
    (props: ScheduleEventData) => (
      <CalendarEvent
        event={props}
        appointmentColor={appointmentColor}
        appointmentCategories={appointmentCategories}
        patientStatuses={patientStatuses}
        isWeekView={isWeekView}
        hasEhrBooking={hasEhrBooking}
        showConsultationStaffInitials={showConsultationStaffInitials}
      />
    ),
    [
      appointmentColor,
      appointmentCategories,
      hasEhrBooking,
      isWeekView,
      patientStatuses,
      showConsultationStaffInitials,
    ],
  )

  const handleEventClick = useCallback(
    (props: EventClickArgs) => {
      const originalEvent = (props as { originalEvent?: Event }).originalEvent
      originalEvent?.preventDefault()
      originalEvent?.stopPropagation()
      setEditedPatient(props.event as IPatientSchedule)
    },
    [setEditedPatient],
  )

  const handleRemoveSelectedCells = useCallback(() => {
    document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
      el.classList.remove('e-selected-cell')
    })
  }, [])

  const handleChange = useCallback(
    async (props: ActionEventArgs) => {
      if (props.requestType !== 'eventChange') {
        return
      }

      handleScheduleChange(props.data as IPatientSchedule).finally(() => {
        handleRemoveSelectedCells()
        requestAnimationFrame(() => reapplyAppointmentColors())
      })
    },
    [handleScheduleChange, handleRemoveSelectedCells, reapplyAppointmentColors],
  )

  const handleDataBound = useCallback(() => {
    scheduleObj.current?.hideSpinner()
    reapplyAppointmentColors()
    applyDynamicColumnWidths()
  }, [scheduleObj, reapplyAppointmentColors, applyDynamicColumnWidths])

  const handleActionFailure = useCallback(() => {
    scheduleObj.current?.hideSpinner()
  }, [scheduleObj])

  const handlePopupOpen = useCallback((props: PopupOpenEventArgs) => {
    if (props.type === 'Editor') {
      props.cancel = true
    }
  }, [])

  const majorSlotTemplate = useCallback(
    (props: { date: Date }) => (
      <div className='e-time-slot-wrap'>{dayjs(props.date).format('h:mm A')}</div>
    ),
    [],
  )

  const selectedDate = useMemo(() => {
    if (!day || !timeZone) return new Date()
    return dayjs(day).tz(timeZone).toDate()
  }, [day, timeZone])

  const timeScale = useMemo(
    () => ({ interval, slotCount: 1, majorSlotTemplate }),
    [interval, majorSlotTemplate],
  )

  const workHours = useMemo(
    () => ({ highlight: true, start: dayInterval.min, end: dayInterval.max }),
    [dayInterval.min, dayInterval.max],
  )

  const eventSettings = useMemo(
    () => ({
      dataSource: data,
      enableTooltip: false,
      enableIndicator: true,
      fields: {
        id: 'id',
        startTime: { title: 'From', name: 'start_time' },
        endTime: { title: 'To', name: 'end_time' },
      },
    }),
    [data],
  )

  return {
    eventRendered,
    eventTemplate,
    handleEventClick,
    handleChange,
    handleDataBound,
    handleActionFailure,
    handlePopupOpen,
    handleRemoveSelectedCells,
    reapplyAppointmentColors,
    majorSlotTemplate,
    selectedDate,
    timeScale,
    workHours,
    eventSettings,
  }
}
